import React, { useState } from 'react';
import { AlertTriangle, Info, AlertCircle, XCircle, Zap, ChevronDown, ChevronUp, Code, FileText, Lock, Star } from 'lucide-react';
import UpgradeButton from './UpgradeButton.tsx';
import type { ScanFinding } from '../types/scan.ts';

interface ResultCardProps {
  finding: ScanFinding;
  showUpgradeButton?: boolean;
  isProUser?: boolean;
  className?: string;
  onJumpToFinding?: (finding: ScanFinding) => void;
}

// Severity chip component
const SeverityChip: React.FC<{ severity: string }> = ({ severity }) => {
  const getSeverityConfig = (sev: string) => {
    switch (sev) {
      case 'critical':
        return {
          label: 'Critical',
          icon: Zap,
          className: 'bg-red-600 text-white border-red-600',
        };
      case 'high':
        return {
          label: 'High',
          icon: XCircle,
          className: 'bg-red-500 text-white border-red-500',
        };
      case 'medium':
        return {
          label: 'Medium',
          icon: AlertTriangle,
          className: 'bg-yellow-500 text-white border-yellow-500',
        };
      case 'low':
        return {
          label: 'Low',
          icon: AlertCircle,
          className: 'bg-blue-500 text-white border-blue-500',
        };
      default:
        return {
          label: 'Info',
          icon: Info,
          className: 'bg-gray-500 text-white border-gray-500',
        };
    }
  };

  const config = getSeverityConfig(severity);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}
      title={`${config.label} severity`}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Generate plain language impact statement
const generateImpactStatement = (finding: ScanFinding): string => {
  const severity = finding.severity;
  const issueType = finding.issue_type || finding.type || 'Issue';
  
  const templates = {
    critical: 'This critical security issue could allow attackers to access your system immediately and requires immediate attention.',
    high: 'This security vulnerability could be exploited to compromise your application and should be addressed quickly.',
    medium: 'This security issue could potentially be exploited under certain conditions and should be addressed to improve security.',
    low: 'This represents a minor security concern that should be addressed over time to maintain best practices.',
    info: 'This is informational and helps you understand your code better.',
  };

  return templates[severity as keyof typeof templates] || templates.medium;
};

export const ResultCard: React.FC<ResultCardProps> = ({ 
  finding, 
  showUpgradeButton = true,
  isProUser = false,
  className = '',
  onJumpToFinding
}) => {
  const [showDeveloperDetails, setShowDeveloperDetails] = useState(false);

  // Handle both new and legacy field names
  const isPremium = finding.is_premium ?? finding.premium ?? false;
  const filePath = finding.file_path || finding.filePath || '';
  const context = finding.context || (finding.line ? `Line ${finding.line}` : '');
  const recommendation = finding.recommendation || finding.message || '';
  const issueType = finding.issue_type || finding.type || '';

  // Generate impact statement
  const impactStatement = generateImpactStatement(finding);

  console.log('[ResultCard] Rendering finding:', {
    id: finding.id,
    type: finding.type,
    severity: finding.severity,
    premium: finding.premium,
  });

  // Enhanced locked overlay for premium features
  const PremiumOverlay = () => (
    <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-amber-600/10 backdrop-blur-[1px] rounded-lg flex items-center justify-center">
      <div className="text-center p-4">
        <div className="bg-white/95 rounded-lg p-4 shadow-lg border border-yellow-300 max-w-xs">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-yellow-600" />
            <Star className="w-5 h-5 text-yellow-500" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Premium Security Insight</h3>
          <p className="text-xs text-gray-600 mb-3">
            Unlock detailed code analysis, exact locations, and step-by-step fix recommendations
          </p>
          {showUpgradeButton && (
            <UpgradeButton
              label="Upgrade to Pro"
              className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded-md transition-colors"
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <div
        className={`p-4 rounded-lg border transition-all duration-200 ${
          isPremium ? 'bg-gray-50/60 border-gray-300' : 'bg-white border-gray-200'
        } hover:shadow-md`}
      >
        {/* Header with severity chip and toggle */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <SeverityChip severity={finding.severity} />
            {isPremium && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                <Lock className="w-3 h-3" />
                Pro
              </span>
            )}
          </div>
          {!isPremium && (
            <button
              onClick={() => setShowDeveloperDetails(!showDeveloperDetails)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              title={showDeveloperDetails ? 'Show plain language' : 'Show developer details'}
            >
              {showDeveloperDetails ? (
                <>
                  <FileText className="w-3 h-3" />
                  Plain
                  <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  <Code className="w-3 h-3" />
                  Details
                  <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Issue Title */}
        <h4 className="text-base font-bold text-gray-900 mb-2">
          {issueType}
        </h4>

        {/* Plain Language Impact (Default View) */}
        {!showDeveloperDetails && !isPremium && (
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700 font-medium">
                {impactStatement}
              </p>
            </div>
            <div className="flex items-center justify-between">
              {filePath && (
                <div className="text-xs text-muted bg-gray-50 rounded px-2 py-1">
                  📁 {filePath}
                </div>
              )}
              {onJumpToFinding && finding.line && filePath && (
                <button
                  onClick={() => onJumpToFinding(finding)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                  title={`Jump to line ${finding.line} in ${filePath}`}
                >
                  <Code className="w-3 h-3" />
                  View Code
                </button>
              )}
            </div>
          </div>
        )}

        {/* Developer Details (Expanded View) */}
        {showDeveloperDetails && !isPremium && (
          <div className="space-y-3">
            {/* File Path */}
            {filePath && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <FileText className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                    {filePath}
                  </span>
                </div>
                {onJumpToFinding && finding.line && filePath && (
                  <button
                    onClick={() => onJumpToFinding(finding)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    title={`Jump to line ${finding.line} in ${filePath}`}
                  >
                    <Code className="w-3 h-3" />
                    View Code
                  </button>
                )}
              </div>
            )}

            {/* Context */}
            {context && (
              <div className="text-sm text-gray-700">
                <span className="font-medium">Context:</span> {context}
              </div>
            )}

            {/* Impact Statement */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">Impact</p>
                  <p className="text-sm text-amber-700">{impactStatement}</p>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            {recommendation && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="mt-4 font-medium text-blue-800 mb-1">Recommendation</p>
                <p className="text-sm text-blue-700">{recommendation}</p>
              </div>
            )}
          </div>
        )}

        {/* Premium Content for Pro Users - Show full details by default */}
        {isPremium && isProUser && (
          <div className="space-y-3">
            {/* File Path and View Code Button */}
            {filePath && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <FileText className="w-3 h-3 text-blue-500" />
                  <span className="text-gray-600 font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">
                    {filePath}
                  </span>
                </div>
                {onJumpToFinding && finding.line && filePath && (
                  <button
                    onClick={() => onJumpToFinding(finding)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    title={`Jump to line ${finding.line} in ${filePath}`}
                  >
                    <Code className="w-3 h-3" />
                    View Code
                  </button>
                )}
              </div>
            )}

            {/* Context */}
            {context && (
              <div className="text-sm text-gray-700">
                <span className="font-medium text-blue-700">Context:</span> {context}
              </div>
            )}

            {/* Impact Statement */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">Impact</p>
                  <p className="text-sm text-amber-700">{impactStatement}</p>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            {recommendation && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="mt-4 font-medium text-blue-800 mb-1">Pro Recommendation</p>
                <p className="text-sm text-blue-700">{recommendation}</p>
              </div>
            )}

            {/* Pro Badge */}
            <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
              <Star className="w-3 h-3" />
              Premium security analysis
            </div>
          </div>
        )}

        {/* Premium Content Placeholder - Only for non-Pro users */}
        {isPremium && !isProUser && (
          <div className="text-sm text-gray-500 italic">
            Premium security analysis with detailed code snippets and fix recommendations available with Pro upgrade.
          </div>
        )}
      </div>

      {/* Premium Overlay - Only show if premium finding AND user is not Pro */}
      {isPremium && !isProUser && <PremiumOverlay />}
    </div>
  );
}; 